import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import * as Core from '../../../core';
import { HollowButton, SolidButton } from '../../../components/buttons-visuals';
import InfoMessage from '../../../components/infoMessage';
import { MatchService } from '../../../services/matchService';

export interface QuizWhizzerJoinLink {
    matchParticipantId: string;
    name: string;
    joinUrl: string;
    teamName?: string;
}

export interface QuizWhizzerTitleMetadata {
    schemaVersion?: string;
    accessCode?: string;
    joinLinks?: QuizWhizzerJoinLink[];
}

export const parseQuizWhizzerTitleMetadata = (titleMetadata?: string): QuizWhizzerTitleMetadata | null => {
    if (!titleMetadata?.trim()) return null;
    try {
        return JSON.parse(titleMetadata) as QuizWhizzerTitleMetadata;
    } catch {
        return null;
    }
};

export const quizWhizzerMatchGameNeedsAccessCode = (matchGame: Core.Models.MatchGame): boolean => {
    const s = matchGame.titleIntegrationState;
    if (
        s === Core.Models.TitleIntegrationState.PendingConfiguration ||
        s === Core.Models.TitleIntegrationState.Failed
    )
        return true;
    if (s === undefined && !matchGame.titleMetadata) return true;
    return false;
};

interface QuizWhizzerAccessCodeSetupProps {
    matchId: string;
    matchGame: Core.Models.MatchGame;
    /** Season host/admin — paste code, manage links, import/reset. */
    canConfigureAsHost: boolean;
    canImportScores: boolean;
    /** Match participant ids the current user belongs to / can edit (for personal join link). */
    myMatchParticipantIds: string[];
    onUpdated: () => Promise<void>;
}

const QuizWhizzerAccessCodeSetup = ({
    matchId,
    matchGame,
    canConfigureAsHost,
    canImportScores,
    myMatchParticipantIds,
    onUpdated,
}: QuizWhizzerAccessCodeSetupProps): JSX.Element => {
    const [accessCode, setAccessCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const needsAccessCode = quizWhizzerMatchGameNeedsAccessCode(matchGame);
    const metadata = useMemo(() => parseQuizWhizzerTitleMetadata(matchGame.titleMetadata), [matchGame.titleMetadata]);
    const joinLinks = metadata?.joinLinks ?? [];
    const configuredCode = metadata?.accessCode;

    const myJoinLink = useMemo(() => {
        if (!myMatchParticipantIds.length || !joinLinks.length) return undefined;
        const mine = new Set(myMatchParticipantIds);
        return joinLinks.find((l) => mine.has(l.matchParticipantId));
    }, [joinLinks, myMatchParticipantIds]);

    const showHostReset =
        canConfigureAsHost &&
        (matchGame.titleIntegrationState === Core.Models.TitleIntegrationState.Provisioned ||
            matchGame.titleIntegrationState === Core.Models.TitleIntegrationState.Failed ||
            !!configuredCode);

    const submitAccessCode = useCallback(async () => {
        const code = accessCode.trim();
        if (!code || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await MatchService.applyMatchGameTitleSetup({
                matchId,
                matchGameId: matchGame.id,
                configuration: { accessCode: code },
            });
            toast.success('Access code saved — join links are ready.');
            setAccessCode('');
            await onUpdated();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsSubmitting(false);
        }
    }, [accessCode, isSubmitting, matchGame.id, matchId, onUpdated]);

    const resetSetup = useCallback(async () => {
        if (isResetting) return;
        if (!window.confirm('Reset QuizWhizzer setup? This clears the access code and join links.')) return;
        setIsResetting(true);
        try {
            await MatchService.resetMatchGameTitleSetup({ matchId, matchGameId: matchGame.id });
            toast.success('Setup reset — paste a new access code.');
            await onUpdated();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsResetting(false);
        }
    }, [isResetting, matchGame.id, matchId, onUpdated]);

    const importScores = useCallback(async () => {
        if (isImporting) return;
        setIsImporting(true);
        try {
            await MatchService.submitMatchGameResult({
                matchGameId: matchGame.id,
                autoScoringData: JSON.stringify({ pull: true }),
                scores: [],
                screenshots: [],
            });
            toast.success('Import started — scores will appear when QuizWhizzer returns FINISHED results.');
            await onUpdated();
        } catch (e) {
            toast.error(Core.API.getErrorMessage(e));
        } finally {
            setIsImporting(false);
        }
    }, [isImporting, matchGame.id, onUpdated]);

    const copyAllLinks = useCallback(async () => {
        if (!joinLinks.length) return;
        const text = joinLinks.map((l) => `${l.teamName || l.name}: ${l.joinUrl}`).join('\n');
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Join links copied.');
        } catch {
            toast.error('Could not copy join links.');
        }
    }, [joinLinks]);

    return (
        <div className="quizwhizzer-access-code-setup mb2x">
            {needsAccessCode && (
                <>
                    {canConfigureAsHost ? (
                        <>
                            <p className="mb">
                                <strong>Game {matchGame.sortOrder} — QuizWhizzer access code.</strong> Create the game
                                in QuizWhizzer, then paste its access code here. Participants will get join links
                                automatically.
                            </p>
                            <div className="disp-flex align-center gap mb">
                                <input
                                    className="form-control"
                                    disabled={isSubmitting}
                                    onChange={(e) => setAccessCode(e.target.value)}
                                    placeholder="Access code"
                                    type="text"
                                    value={accessCode}
                                />
                                <SolidButton
                                    as="button"
                                    disabled={isSubmitting || !accessCode.trim()}
                                    onClick={() => void submitAccessCode()}
                                    size="medium"
                                >
                                    {isSubmitting ? 'Saving…' : 'Save code'}
                                </SolidButton>
                            </div>
                        </>
                    ) : (
                        <InfoMessage
                            message={`Game ${matchGame.sortOrder}: waiting for the host to set up QuizWhizzer. Your join link will appear here when the access code is ready.`}
                            type="info"
                        />
                    )}
                </>
            )}

            {!needsAccessCode && configuredCode && (
                <>
                    {myJoinLink ? (
                        <div className="mb2x">
                            <InfoMessage
                                message={`Game ${matchGame.sortOrder} is in progress. Join with your locked name (${myJoinLink.name}) so scores match correctly.`}
                                type="info"
                            />
                            <SolidButton
                                as="a"
                                className="mt"
                                href={myJoinLink.joinUrl}
                                rel="noreferrer"
                                size="medium"
                                target="_blank"
                            >
                                Join QuizWhizzer session
                            </SolidButton>
                        </div>
                    ) : (
                        !canConfigureAsHost && (
                            <InfoMessage
                                message={`Game ${matchGame.sortOrder} is in progress on QuizWhizzer. Ask your host for a join link if you don’t see one here.`}
                                type="info"
                            />
                        )
                    )}

                    {canConfigureAsHost && (
                        <>
                            <p className="mb">
                                <strong>Game {matchGame.sortOrder} — access code `{configuredCode}`.</strong> Session is
                                live. Import scores after the QuizWhizzer host ends the game.
                            </p>
                            {joinLinks.length > 0 && (
                                <div className="mb2x">
                                    <div className="disp-flex justify-between align-center mb">
                                        <strong>All join links</strong>
                                        <HollowButton as="button" onClick={() => void copyAllLinks()} size="small">
                                            Copy all
                                        </HollowButton>
                                    </div>
                                    <ul className="mb0">
                                        {joinLinks.map((link) => (
                                            <li key={link.matchParticipantId} className="mb">
                                                <div>
                                                    <strong>{link.teamName || 'Participant'}</strong> as{' '}
                                                    <code>{link.name}</code>
                                                </div>
                                                <a href={link.joinUrl} rel="noreferrer" target="_blank">
                                                    {link.joinUrl}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {canImportScores && !matchGame.isComplete && (
                                <SolidButton
                                    as="button"
                                    disabled={isImporting}
                                    onClick={() => void importScores()}
                                    size="medium"
                                >
                                    {isImporting ? 'Importing…' : 'Import QuizWhizzer scores'}
                                </SolidButton>
                            )}
                        </>
                    )}
                </>
            )}

            {showHostReset && (
                <div className="mt">
                    <HollowButton as="button" disabled={isResetting} onClick={() => void resetSetup()} size="medium">
                        {isResetting ? 'Resetting…' : 'Reset QuizWhizzer setup (host only)'}
                    </HollowButton>
                </div>
            )}
        </div>
    );
};

export default QuizWhizzerAccessCodeSetup;
